// assistant-model.ts — the assistant overlay's DOM-free logic (work item
// 64, PR 1; design doc helm-console/docs/assistant-agent.md). Everything a
// smoke test can exercise without a browser lives here: the transcript
// fold (append-only events -> ordered items, idempotent by seq), the
// queue, the tab-strip derivations, and the localStorage echo shapes.
//
// Lives in pi-stack because BOTH clients mount the overlay (the Bridge
// home screen and the Helm release UI) — the configurePiViewHost precedent:
// one implementation, host-injected seams, no cross-package relative
// imports.
//
// ACTION CARDS ARE FIRST-CLASS: every agent-performed action arrives as an
// `action` event whose payload is the typed card {action, target, outcome,
// detail?, ts}. The fold renders one transcript item per card, identically
// live and on replay — an agent that acts invisibly is not auditable by
// the person it acts for. (The source pattern this UI ports had NO
// per-tool cards; this is deliberate new construction.)

/** One event off the wire (the SSE `data:` body / the replay rows). */
export interface AssistantEventRecord {
  seq: number;
  tab_id: string;
  kind: string;
  payload: Record<string, unknown>;
  ts: string;
}

/** One tab row from GET /api/assistant/tabs — the server-truth strip. */
export interface AssistantTabRow {
  id: string;
  title: string;
  status: string;
  unread: boolean;
  archived: boolean;
  created_at: string;
  updated_at: string;
}

/** The typed card behind every agent-performed action.
 *
 * `confirm-required` is the CONSENT-PENDING state, and it is not a failure:
 * the console's confirmGate has proposed a side-effectful act and is waiting
 * for the user to answer in chat. It was minted server-side (2026-09-08,
 * helm-console/src/server/assistant-tools.ts `AssistantCard`) and this union
 * never learned it, so `normalizeActionCard` below folded it into 'error' as
 * a malformation — which is the whole of the owner's 2026-09-09 report: an
 * assistant asking "May I PUT .../gate-policy?" rendered as a RED ERROR BOX
 * (`Result — error`), with the question as its body. Any outcome added to
 * AssistantCard must be added HERE and to OUTCOMES, or the fold silently
 * downgrades it. */
export interface ActionCard {
  action: string;
  target: string | null;
  outcome: 'ok' | 'refused' | 'error' | 'unavailable' | 'confirm-required';
  detail: string | null;
  ts: number;
  /** confirm-required only: the proposal's fingerprint (the console matches
   * the user's answer to the exact act). Carried so the pending prompt can
   * key on the proposal rather than on its prose. */
  fp?: string;
}

export type TranscriptItem =
  | { kind: 'user'; id: string; text: string; ts: number }
  | { kind: 'assistant'; id: string; text: string; streaming: boolean; stoppedEarly: boolean; meta: Record<string, unknown> | null }
  | { kind: 'action'; card: ActionCard }
  /** A thinking block — one row in the step timeline, collapsed to
   * "Thought for 2 sec" and expanding to the prose (the Helm's own idiom;
   * the host emits start/end, never per-token). */
  | { kind: 'thinking'; id: string; text: string; ms: number | null; streaming: boolean; ts: number }
  | { kind: 'context'; summary: string; ts: number }
  | { kind: 'error'; message: string; ts: number };

/** The transcript-item kinds that fold into ONE collapsible step group — the
 * Helm's segment contract (owner item #20): a run of steps, broken by prose.
 * The overlay and the chat therefore group identically. */
export function isStepItem(item: TranscriptItem): boolean {
  return item.kind === 'thinking' || item.kind === 'action' || item.kind === 'context';
}

export interface QueuedItem {
  id: string;
  text: string;
  ts: number;
}

export interface TabTranscript {
  items: TranscriptItem[];
  queue: QueuedItem[];
  /** Live run phase (status events); null when settled. */
  phase: string | null;
  note: string | null;
  /** Last applied seq — the SSE reconnect cursor. */
  cursor: number;
  loaded: boolean;
}

export function emptyTranscript(): TabTranscript {
  return { items: [], queue: [], phase: null, note: null, cursor: 0, loaded: false };
}

export type AssistantChange = 'items' | 'queue' | 'phase' | 'none' | 'stale';

/** MUST mirror ActionCard['outcome'] — an outcome absent here is normalized
 * to 'error', which is a lie the user reads as a failure. */
const OUTCOMES = new Set(['ok', 'refused', 'error', 'unavailable', 'confirm-required']);

/** Outcomes that mean the step FAILED. `confirm-required` is deliberately not
 * one: the act has not been attempted. The overlay reads this rather than
 * `outcome !== 'ok'`, which was the second half of the same defect. */
export function isFailedOutcome(outcome: ActionCard['outcome']): boolean {
  return outcome !== 'ok' && outcome !== 'confirm-required';
}

/** Coerce an `action` payload into the typed card — unknown shapes become
 * an explicit 'error'-outcome card naming the malformation, never a throw
 * (a malformed frame must not kill the fold). */
export function normalizeActionCard(raw: unknown): ActionCard {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const action = typeof o.action === 'string' && o.action ? o.action : 'unknown-action';
  const outcome = typeof o.outcome === 'string' && OUTCOMES.has(o.outcome)
    ? (o.outcome as ActionCard['outcome'])
    : 'error';
  const cardOut: ActionCard = {
    action,
    target: typeof o.target === 'string' && o.target ? o.target : null,
    outcome,
    detail: typeof o.detail === 'string' && o.detail ? o.detail : null,
    ts: typeof o.ts === 'number' && Number.isFinite(o.ts) ? o.ts : 0,
  };
  if (typeof o.fp === 'string' && o.fp) cardOut.fp = o.fp;
  return cardOut;
}

/** THE STANDING QUESTION, if there is one: the newest `confirm-required` card
 * that the user has NOT yet answered.
 *
 * The console's proof of consent is structural — a `user-message` event with a
 * higher seq than the proposal — so "unanswered" is exactly "no user item after
 * it", read off the same ordered transcript. Returns null when the newest
 * proposal has been answered (the act is the model's to make now) or when a
 * LATER card for the same action reports a real outcome, which is how a
 * proposal the user declined stops nagging.
 *
 * This is what makes the gate ANSWERABLE. Before it, the question was the
 * `detail` of a collapsed step row inside a collapsed step group, so the user
 * had to expand twice to find that they were being asked anything at all. */
export function pendingConfirm(items: TranscriptItem[]): ActionCard | null {
  /** Terminal cards seen AFTER the proposal, keyed action+target — a
   * confirm-required card carries the fingerprint and a settled one does not,
   * so the act's own identity is the join. An unrelated tool card that ran
   * after the proposal (the agent kept reading while it waited) must NOT end
   * the question, which is why this is keyed rather than positional. */
  const settled = new Set<string>();
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i]!;
    if (item.kind === 'user') return null; // the user has spoken since
    if (item.kind !== 'action') continue;
    const key = `${item.card.action}\u0000${item.card.target ?? ''}`;
    if (item.card.outcome !== 'confirm-required') {
      settled.add(key);
      continue;
    }
    if (settled.has(key)) return null; // this act already reported an outcome
    return item.card;
  }
  return null;
}

/** The two answers the consent affordance sends. They are ORDINARY USER
 * MESSAGES on the ordinary channel — the button types for you; it does not
 * open a second, privileged consent lane the model could reach. */
export const CONFIRM_YES = 'Yes, go ahead.';
export const CONFIRM_NO = 'No — do not do that.';

/** The sentence to put in front of the user, lifted from the proposal card.
 * The console writes the ask into `detail`; a card with none still gets an
 * honest prompt naming the action rather than an empty question. */
export function confirmAsk(card: ActionCard): string {
  const d = (card.detail ?? '').trim();
  if (d) return d;
  return card.target ? `May I ${card.target}?` : `May I run ${card.action}?`;
}

/** Settle every assistant item still marked streaming — a segment boundary
 * (a new messageId, a tool card, a context read) or the turn's end. A
 * transcript may hold at most ONE streaming item, and only at its tail;
 * this is the invariant the render lane's in-place patch relies on.
 * Returns how many it settled (the smoke's measured count). */
export function settleStreaming(state: TabTranscript): number {
  let n = 0;
  for (const i of state.items) {
    if ((i.kind === 'assistant' || i.kind === 'thinking') && i.streaming) {
      i.streaming = false;
      n++;
    }
  }
  return n;
}

/** Fold ONE event into a tab's transcript. Returns what changed so the
 * view re-renders only that region. Idempotent by seq: a replayed or
 * duplicated event is refused as 'stale' — reconnect-with-cursor plus this
 * guard makes at-least-once delivery render exactly-once. */
export function applyAssistantEvent(state: TabTranscript, ev: AssistantEventRecord): AssistantChange {
  if (!Number.isFinite(ev.seq) || ev.seq <= state.cursor) return 'stale';
  state.cursor = ev.seq;
  const p = (ev.payload && typeof ev.payload === 'object' ? ev.payload : {}) as Record<string, unknown>;
  switch (ev.kind) {
    case 'user-message': {
      state.items.push({
        kind: 'user',
        id: typeof p.id === 'string' ? p.id : `u-${ev.seq}`,
        text: typeof p.text === 'string' ? p.text : '',
        ts: typeof p.ts === 'number' ? p.ts : Date.parse(ev.ts) || 0,
      });
      return 'items';
    }
    case 'text': {
      const messageId = typeof p.messageId === 'string' ? p.messageId : `a-${ev.seq}`;
      const delta = typeof p.delta === 'string' ? p.delta : '';
      const last = state.items[state.items.length - 1];
      if (last && last.kind === 'assistant' && last.streaming && last.id === messageId) {
        last.text += delta;
      } else {
        // A NEW message segment (the host mints one messageId per pi
        // assistant message — a tool call splits a turn into
        // text / card / text). The segment before it is finished: settle
        // it, or it streams forever (the owner-measured garble, 2026-09-08 —
        // see settleStreaming).
        settleStreaming(state);
        state.items.push({ kind: 'assistant', id: messageId, text: delta, streaming: true, stoppedEarly: false, meta: null });
      }
      return 'items';
    }
    case 'action': {
      // A card means pi ran a tool, which only happens AFTER the assistant
      // message that requested it ended — the text segment before this
      // card is complete.
      settleStreaming(state);
      state.items.push({ kind: 'action', card: normalizeActionCard(p.card ?? p) });
      return 'items';
    }
    case 'thinking': {
      // A thought opens with `streaming: true` and settles by id — the live
      // row shimmers "Thinking…", then reads "Thought for N sec".
      const id = typeof p.id === 'string' ? p.id : `th-${ev.seq}`;
      const streaming = p.streaming === true;
      const ts = typeof p.ts === 'number' ? p.ts : Date.parse(ev.ts) || 0;
      const open = state.items.find(
        (i): i is Extract<TranscriptItem, { kind: 'thinking' }> => i.kind === 'thinking' && i.id === id,
      );
      if (open) {
        if (typeof p.text === 'string') open.text = p.text;
        if (typeof p.ms === 'number') open.ms = p.ms;
        open.streaming = streaming;
      } else {
        settleStreaming(state); // a thought begins where the prose before it ended
        state.items.push({
          kind: 'thinking',
          id,
          text: typeof p.text === 'string' ? p.text : '',
          ms: typeof p.ms === 'number' ? p.ms : null,
          streaming,
          ts,
        });
      }
      return 'items';
    }
    case 'context-read': {
      settleStreaming(state); // the screen_context tool ran — same boundary as a card
      state.items.push({
        kind: 'context',
        summary: typeof p.summary === 'string' ? p.summary : 'screen context read',
        ts: Date.parse(ev.ts) || 0,
      });
      return 'items';
    }
    case 'status': {
      state.phase = typeof p.phase === 'string' ? p.phase : null;
      state.note = typeof p.note === 'string' ? p.note : null;
      return 'phase';
    }
    case 'queued': {
      const m = (p.message && typeof p.message === 'object' ? p.message : {}) as Record<string, unknown>;
      state.queue.push({
        id: typeof m.id === 'string' ? m.id : `q-${ev.seq}`,
        text: typeof m.text === 'string' ? m.text : '',
        ts: typeof m.ts === 'number' ? m.ts : 0,
      });
      return 'queue';
    }
    case 'dequeued': {
      const ids = Array.isArray(p.ids) ? p.ids.filter((x): x is string => typeof x === 'string') : [];
      const before = state.queue.length;
      state.queue = state.queue.filter((q) => !ids.includes(q.id));
      return state.queue.length === before ? 'none' : 'queue';
    }
    case 'done': {
      const messageId = typeof p.messageId === 'string' ? p.messageId : null;
      const item = [...state.items].reverse().find(
        (i): i is Extract<TranscriptItem, { kind: 'assistant' }> =>
          i.kind === 'assistant' && (messageId ? i.id === messageId : i.streaming),
      );
      if (item) {
        item.streaming = false;
        item.meta = (p.meta && typeof p.meta === 'object' ? (p.meta as Record<string, unknown>) : null);
        // A done with server-authoritative text wins over a partial stream.
        // done.text is the LAST segment's accumulation (translate.ts resets
        // per message_start), so it only ever lands on the id it names.
        if (typeof p.text === 'string' && p.text.length >= item.text.length) item.text = p.text;
      } else if (typeof p.text === 'string' && p.text) {
        state.items.push({ kind: 'assistant', id: messageId ?? `a-${ev.seq}`, text: p.text, streaming: false, stoppedEarly: false, meta: null });
      }
      // The turn is over: EVERY segment settles, not just the one done
      // names. Before this, a turn shaped text/card/text left its first
      // segment streaming forever, and the render lane's "the streaming
      // row" address then pointed at that stale row for the rest of the
      // tab's life — every later bubble froze at its first delta ("I" …
      // card … "Highlighted"; owner transcript 2026-09-08).
      settleStreaming(state);
      state.phase = null;
      state.note = null;
      return 'items';
    }
    case 'stopped': {
      const item = [...state.items].reverse().find(
        (i): i is Extract<TranscriptItem, { kind: 'assistant' }> => i.kind === 'assistant' && i.streaming,
      );
      if (item) {
        item.streaming = false;
        item.stoppedEarly = true;
      }
      settleStreaming(state); // earlier segments of the stopped turn settle too
      state.phase = null;
      state.note = null;
      return 'items';
    }
    case 'error': {
      state.items.push({
        kind: 'error',
        message: typeof p.message === 'string' ? p.message : 'unknown error',
        ts: Date.parse(ev.ts) || 0,
      });
      state.phase = null;
      state.note = null;
      return 'items';
    }
    default:
      // Forward-compat: a newer server vocabulary is ignored by name,
      // never a throw — the cursor already advanced above.
      return 'none';
  }
}

// ---------------------------------------------------------------------------
// Tab-strip derivations

/** Strip order: live (non-archived) tabs, server order (updated_at desc). */
export function visibleTabs(tabs: AssistantTabRow[]): AssistantTabRow[] {
  return tabs.filter((t) => !t.archived);
}

/** Badge count: unread live tabs. */
export function unreadCount(tabs: AssistantTabRow[]): number {
  return visibleTabs(tabs).filter((t) => t.unread).length;
}

const BUSY = new Set(['provisioning', 'running']);

export function isBusyStatus(status: string): boolean {
  return BUSY.has(status);
}

/** Which tabs need a live SSE subscription: the active tab (transcript on
 * screen) plus every busy tab (background tabs stream too — unread and
 * status move without focus). */
export function streamTabIds(tabs: AssistantTabRow[], activeTabId: string | null): string[] {
  const out: string[] = [];
  for (const t of visibleTabs(tabs)) {
    if (t.id === activeTabId || isBusyStatus(t.status)) out.push(t.id);
  }
  return out;
}

// ---------------------------------------------------------------------------
// The localStorage echo — {open, expanded, activeTabId} is a per-user UI
// convenience, NEVER truth (the tab list is the server's). Parse must
// survive garbage; storage failures fall back to defaults silently.

export interface AssistantUiEcho {
  open: boolean;
  expanded: boolean;
  activeTabId: string | null;
}

export const ASSISTANT_ECHO_KEY = 'assistant.ui.v1';

export function defaultEcho(): AssistantUiEcho {
  return { open: false, expanded: false, activeTabId: null };
}

export function parseEcho(raw: string | null): AssistantUiEcho {
  if (!raw) return defaultEcho();
  try {
    const o = JSON.parse(raw) as Record<string, unknown>;
    return {
      open: o.open === true,
      expanded: o.expanded === true,
      activeTabId: typeof o.activeTabId === 'string' ? o.activeTabId : null,
    };
  } catch {
    return defaultEcho();
  }
}

export function serializeEcho(e: AssistantUiEcho): string {
  return JSON.stringify({ open: e.open, expanded: e.expanded, activeTabId: e.activeTabId });
}

// ---------------------------------------------------------------------------
// Reconnect backoff (the deployments.ts ladder: 1s -> x2 -> 30s ceiling,
// reset on open).

export const BACKOFF_START_MS = 1000;
export const BACKOFF_CEILING_MS = 30_000;

export function nextBackoff(ms: number): number {
  return Math.min(Math.max(ms, BACKOFF_START_MS) * 2, BACKOFF_CEILING_MS);
}

// ---------------------------------------------------------------------------
// Screen context (PR 3) — the DATA contract the overlay pushes while open:
// surface + route + selected identifiers, NEVER pixels or content. Hosts
// contribute through a provider seam; the builder is pure so the smoke can
// pin the shape.

export interface ScreenContextBasics {
  hash: string;
  title: string;
}

export function buildScreenContext(
  basics: ScreenContextBasics,
  provided: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (provided && typeof provided === 'object') {
    for (const [k, v] of Object.entries(provided)) {
      if (v !== undefined && v !== null && v !== '') out[k] = v;
    }
  }
  // Basics never lose to a provider typo, but a provider may extend them.
  out.hash = basics.hash;
  out.title = basics.title;
  if (typeof out.surface !== 'string') out.surface = 'unknown';
  return out;
}

/** The AFFORDANCES half of the screen-context contract (work item 64): what
 * the agent may point at, and what it may run. Still a DATA contract — ids
 * and flow names, never content, never pixels.
 *
 * Sources are the product's OWN curated onboarding registry, so the agent
 * cannot invent a selector: `targets` is every DOM id a registered step
 * spotlights, filtered to the ones actually on this page; `flows` is every
 * registered flow id. Bounded by `max` so the snapshot stays inside the
 * server's 8 KB cap. PURE — `has` is the DOM probe, injected for the drill.
 */
export function screenAffordances(
  has: (id: string) => boolean,
  targets: string[],
  flows: string[],
  max = 60,
): { spotlight_targets: string[]; available_flows: string[] } {
  const present: string[] = [];
  for (const id of targets) {
    if (present.length >= max) break;
    if (has(id)) present.push(id);
  }
  return { spotlight_targets: present, available_flows: flows.slice(0, max) };
}

/** Change detector for the push throttle — serialized equality is the
 * contract (the snapshot is small by the 8 KB server cap). */
export function sameScreenContext(
  a: Record<string, unknown> | null,
  b: Record<string, unknown> | null,
): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export const CONTEXT_PUSH_MIN_MS = 2000;
